import Box from '@mui/material/Box';
import { isGeneratedReportRangeTypeDynamic } from 'app-components/utils/date/predefinedRange';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import type { PickersShortcutsProps } from '@mui/x-date-pickers/PickersShortcuts';
import type { DateRange } from '@mui/x-date-pickers-pro/models';
import type { DateTime } from 'luxon';
import React from 'react';
import { Divider, Typography } from '@mui/material';

// Extends Partial<PickersShortcutsProps> so CustomShortcuts satisfies MUI's slot contract
// (MUI calls the slot with PickersShortcutsProps; our extras are optional so that type satisfies this one)
type CustomShortcutItem = {
  id: string;
  label: string;
  indent?: boolean; // render this option indented
  dividerAfter?: boolean; // render a divider after this option
};

// Render-only union. Captions are non-clickable and have no range value, so they
// live only at the render layer and never enter the CustomShortcutItem data array.
type ShortcutEntry =
  | { kind: 'caption'; id: string; label: string }
  | { kind: 'option'; id: string; label: string; indent?: boolean; dividerAfter?: boolean };
export type CustomShortcutsProps = Partial<PickersShortcutsProps<DateRange<DateTime>>> & {
  shortcutItems?: CustomShortcutItem[];
  selectedShortcutId?: string;
  handleShortcutSelect?: (id: string) => void;
  customLabel?: string;
  showDynamic?: boolean;
};

const emptyArray: CustomShortcutItem[] = [];

function CustomShortcuts({
  shortcutItems = emptyArray,
  selectedShortcutId,
  handleShortcutSelect,
  customLabel,
  showDynamic,
}: CustomShortcutsProps) {
  const entries: ShortcutEntry[] = [
    ...shortcutItems.map((item) => ({ kind: 'option' as const, ...item })),
    { kind: 'caption', id: 'customRangeCaption', label: 'Custom range' },
    { kind: 'option', id: 'CUSTOM', label: customLabel ?? 'Custom', indent: true },
    ...(showDynamic ? [{ kind: 'option' as const, id: 'DYNAMIC', label: 'Dynamic', indent: true }] : []),
  ];

  return (
    <Box
      sx={{
        gridColumn: 1,
        gridRow: 2,
        borderRight: '1px solid',
        borderColor: 'border.light',
      }}
    >
      <List dense>
        {entries.map((entry, index) => {
          if (entry.kind === 'caption') {
            return (
              <ListItem disablePadding key={entry.id} id={entry.id}>
                <Typography variant='captionRegular' color='textSecondary' sx={{ ml: 1, my: 0.5 }}>
                  {entry.label}
                </Typography>
              </ListItem>
            );
          }
          return (
            <React.Fragment key={entry.id}>
              <ListItem disablePadding>
                <ListItemButton
                  id={`date-range-shortcut-${entry.id}`}
                  data-testid={`date-range-shortcut-${entry.id}`}
                  selected={
                    selectedShortcutId === entry.id ||
                    (entry.id === 'DYNAMIC' && isGeneratedReportRangeTypeDynamic(selectedShortcutId as any))
                  }
                  onClick={() => handleShortcutSelect?.(entry.id)}
                  sx={{
                    borderRadius: '8px',
                    mx: entry.indent ? 1 : 0,
                    '&.Mui-selected': {
                      color: 'primary.main',
                    },
                    '& .MuiListItemText-root': {
                      margin: 0,
                    },
                  }}
                >
                  <ListItemText
                    primary={entry.label}
                    slotProps={{
                      primary: {
                        variant: 'paragraphRegular',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
              {entry.dividerAfter && index !== entries.length - 1 && <Divider sx={{ mx: 1, my: 0.5 }} />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
}

export default CustomShortcuts;
